<script lang="ts">
  import { tick } from "svelte";
  import { tasks, wbsNodes, canEdit, openEditModal, saveWbsNode, openAddActivity } from "../lib/stores";
  import { workPackages, nextFreeWbsCode } from "../lib/wbs";
  export let expanded = false;
  let panelOpen = expanded, managerOpen = false;
  let manager: HTMLDetailsElement;
  let nameInput: HTMLInputElement;
  async function editNode(id: string) {
    selectNode(id); managerOpen = true;
    await tick();
    manager.scrollIntoView({ behavior: "smooth", block: "nearest" });
    nameInput.focus({ preventScroll: true });
  }
  async function addPackage(scopeId: string) {
    selectNode(""); parentId = scopeId;
    const parent = $wbsNodes.find((n) => n.id === scopeId)!;
    code = nextFreeWbsCode($wbsNodes, parent.code);
    managerOpen = true;
    await tick(); manager.scrollIntoView({ behavior: "smooth", block: "nearest" }); nameInput.focus({ preventScroll: true });
  }
  $: editable = $canEdit;
  $: packages = workPackages($wbsNodes);
  $: unassigned = $tasks.filter((t) => !packages.some((n) => n.id === t.wbsId));
  $: scopes = $wbsNodes.filter((n) => n.parentId === "wbs-1").sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  let editId = "", code = "", name = "", parentId = "wbs-1", error = "";
  function selectNode(id: string) {
    editId = id;
    const node = $wbsNodes.find((n) => n.id === id);
    code = node?.code ?? ""; name = node?.name ?? ""; parentId = node?.parentId ?? "wbs-1"; error = "";
  }
  function save() {
    error = saveWbsNode({ id: editId || crypto.randomUUID(), code: code.trim(), name: name.trim(), parentId });
    if (!error) selectNode("");
  }
</script>

<details class="wbs-panel" bind:open={panelOpen}>
  <summary>WBS · Crane Control Upgrade <span>{packages.length} work packages · {unassigned.length} activities to assign</span></summary>
  <p>Scope items organise the work; phase swimlanes organise execution. Several activities can share one work package.</p>
  {#if editable}<button class="btn" type="button" on:click={() => addPackage("wbs-1")}>+ Add scope</button>{/if}
  <div class="wbs-scopes">
    {#each scopes as scope (scope.id)}
      <details open={expanded}>
        <summary>{scope.code} — {scope.name}</summary>
        {#if editable}
          <div class="wbs-actions"><button class="btn" type="button" aria-label={"Edit scope " + scope.code} on:click={() => editNode(scope.id)}>Edit scope</button>
          <button class="btn" type="button" aria-label={"Add work package to " + scope.code} on:click={() => addPackage(scope.id)}>+ Work package</button></div>
        {/if}
        {#each packages.filter((n) => n.parentId === scope.id) as node (node.id)}
          <div class="wbs-package">
            <strong>{node.code} — {node.name}</strong>
            {#if editable}
              <div class="wbs-actions"><button class="btn" type="button" aria-label={"Edit WBS " + node.code} on:click={() => editNode(node.id)}>Edit</button>
              <button class="btn" type="button" aria-label={"Add activity to " + node.code} on:click={() => openAddActivity(node.id)}>+ Add activity</button></div>
            {/if}
            {#each $tasks.filter((t) => t.wbsId === node.id) as t (t.id)}
              <button type="button" class="linkish" on:click={() => openEditModal(t.id)}>{t.name}</button>
            {:else}<span class="empty">No activities assigned</span>{/each}
          </div>
        {/each}
      </details>
    {/each}
  </div>
  {#if unassigned.length}
    <details open>
      <summary>WBS to assign ({unassigned.length})</summary>
      <p>Combined scopes and project-wide milestones need review before assignment. Split work only after agreeing durations and dependencies.</p>
      {#each unassigned as t (t.id)}
        <button type="button" class="linkish pending" on:click={() => openEditModal(t.id)}>{t.name}</button>
      {/each}
    </details>
  {/if}
  <p>Review points: combined PLC/network work and sensor calibration need scope clarification. SWAP + Noordzij needs a work description. Function verification and sway/skew control need a detail check. Confirm the acceptance criteria and dependencies of “All devices communicating” and “Drives tested”. Handover scope is not yet confirmed.</p>
  {#if editable}
    <details bind:this={manager} bind:open={managerOpen}>
      <summary>Manage WBS</summary>
      <form on:submit|preventDefault={save}>
        <label>Work package or scope
          <select value={editId} on:change={(e) => selectNode(e.currentTarget.value)}>
            <option value="">Add new</option>
            {#each $wbsNodes.filter((n) => n.parentId !== null) as node (node.id)}<option value={node.id}>{node.code} — {node.name}</option>{/each}
          </select>
        </label>
        <label>Parent scope
          <select bind:value={parentId} disabled={!!editId}>
            <option value="wbs-1">1 — Crane Control Upgrade (new scope)</option>
            {#each scopes as scope (scope.id)}<option value={scope.id}>{scope.code} — {scope.name}</option>{/each}
          </select>
        </label>
        <label>WBS code<input required bind:value={code} placeholder="e.g. 1.4.5" /></label>
        <label>Name<input bind:this={nameInput} required bind:value={name} /></label>
        {#if error}<p role="alert">{error}</p>{/if}
        <button class="btn" type="submit">{editId ? "Save WBS" : "Add WBS"}</button>
      </form>
    </details>
  {/if}
</details>

<style>
  .wbs-panel { margin: 12px 0 18px; border:1px solid var(--border); border-radius:8px; padding:12px; color:var(--text-secondary); font-size:12px; }
  summary { cursor:pointer; font-weight:600; padding:4px 0; }
  summary span { font-weight:400; margin-left:12px; }
  p { line-height:1.5; color:var(--text-muted); }
  .wbs-actions { display:flex; flex-wrap:wrap; gap:6px; margin:4px 0; }
  .wbs-scopes { margin-top:12px; display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:8px 20px; }
  .wbs-package { display:flex; flex-direction:column; gap:6px; margin:12px 0 12px 8px; }
  .linkish { text-align:left; }
  .pending { display:block; margin:8px; }
  .empty { color:var(--text-muted); }
  form { display:flex; flex-wrap:wrap; gap:12px; align-items:end; margin-top:12px; }
  label { display:flex; flex-direction:column; gap:4px; }
  input, select { padding:6px; max-width:100%; color:var(--text-primary); background:var(--surface-1); border:1px solid var(--border); border-radius:4px; }
</style>
